import React, { useState, useEffect, useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { UserContext } from "../services/UserContext";
import { useClient, canManageClinica } from "../services/ClientContext";
import { API, authHeaders } from "../services/config";
import './PacientesPage.css';

function emptyProfesional() {
  return {
    id: null,
    apellido: "",
    nombre: "",
    dni: "",
    mail: "",
    celular: "",
    matricula: "",
    color: "#0A1B3D",
    activo: true,
    phone: "",
    password: "",
    user_id: null,
    user: null,
  };
}

function tieneAcceso(prof) {
  return Boolean(prof?.user_id || prof?.user?.phone);
}

/** Normaliza fechas del API (YYYY-MM-DD o ISO) a YYYY-MM-DD */
function toDateOnly(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

/** Formatea una fecha para mostrar: DD/MM/YYYY */
function fmtFecha(value) {
  const iso = toDateOnly(value);
  if (!iso || iso.length < 10) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

/** Resuelve la ficha del consultorio autoadministrado */
function resolveMiProf(profesionales, user) {
  if (!user || !profesionales.length) return null;

  const linkedId = user.profesional?.id;
  if (linkedId != null) {
    const byId = profesionales.find((p) => Number(p.id) === Number(linkedId));
    if (byId) return byId;
  }

  const byUserId = profesionales.find((p) => Number(p.user_id) === Number(user.id));
  if (byUserId) return byUserId;

  if (profesionales.length === 1) return profesionales[0];

  return null;
}

export default function ProfesionalesPage() {
  const { user, setUser } = useContext(UserContext);
  const { multiProfesional } = useClient();
  const puedeGestionar = canManageClinica(user, multiProfesional);

  const [profesionales, setProfesionales] = useState([]);
  const [loadingProfesionales, setLoadingProfesionales] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyProfesional());
  const [busqueda, setBusqueda] = useState("");
  const [error, setError] = useState("");
  const [licenciasProf, setLicenciasProf] = useState(null);
  const [horariosProf, setHorariosProf] = useState(null);
  const navigate = useNavigate();

  const refreshSessionProfesional = useCallback(async () => {
    if (!setUser) return;
    try {
      const res = await fetch(`${API}/perfil`, { headers: authHeaders() });
      if (!res.ok) return;
      const perfil = await res.json();
      setUser(perfil);
      localStorage.setItem("user", JSON.stringify(perfil));
    } catch (e) {
      console.error("Error al actualizar sesión:", e);
    }
  }, [setUser]);

  const fetchProfesionales = useCallback(async () => {
    setLoadingProfesionales(true);
    setLoadError("");
    try {
      const res = await fetch(`${API}/profesionales`, { headers: authHeaders() });
      if (res.status === 401) { navigate("/login"); return; }
      const data = await res.json();
      if (data.success) {
        setProfesionales(data.data);
      } else {
        setLoadError("No se pudieron cargar los profesionales.");
      }
    } catch (e) {
      console.error("Error al cargar profesionales:", e);
      setLoadError("Error de conexión al cargar los datos.");
    } finally {
      setLoadingProfesionales(false);
    }
  }, [navigate]);

  useEffect(() => { fetchProfesionales(); }, [fetchProfesionales]);

  useEffect(() => {
    if (multiProfesional || loadingProfesionales || !user || !setUser) return;
    const miProf = resolveMiProf(profesionales, user);
    if (!miProf) return;
    const sessionId = user.profesional?.id;
    if (sessionId != null && Number(sessionId) === Number(miProf.id)) return;
    if (Number(miProf.user_id) === Number(user.id) || profesionales.length === 1) {
      setUser({ ...user, profesional: miProf });
      localStorage.setItem("user", JSON.stringify({ ...user, profesional: miProf }));
    }
  }, [multiProfesional, loadingProfesionales, profesionales, user, setUser]);

  const handleAdd = () => {
    setForm(emptyProfesional());
    setEditId(null);
    setError("");
    setShowForm(true);
  };

  const handleSetupPerfil = () => {
    const unlinked = profesionales.filter((p) => !p.user_id);
    if (unlinked.length === 1) {
      handleEdit(unlinked[0]);
      return;
    }
    const parts = (user?.name || "").trim().split(/\s+/).filter(Boolean);
    const apellido = parts.length > 1 ? parts[parts.length - 1] : "";
    const nombre = parts.length > 1 ? parts.slice(0, -1).join(" ") : (parts[0] || "");
    setForm({
      ...emptyProfesional(),
      apellido,
      nombre,
      mail: user?.email || "",
      celular: user?.phone || "",
    });
    setEditId(null);
    setError("");
    setShowForm(true);
  };

  const handleEdit = (prof) => {
    setForm({
      ...emptyProfesional(),
      ...prof,
      phone: prof.user?.phone || "",
      password: "",
    });
    setEditId(prof.id);
    setError("");
    setShowForm(true);
  };

  const handleLicencias = (prof) => {
    setLicenciasProf(prof);
  };

  const handleHorarios = (prof) => {
    setHorariosProf(prof);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSave = async () => {
    if (!form.apellido.trim() || !form.nombre.trim()) {
      setError("Apellido y nombre son obligatorios.");
      return;
    }

    if (multiProfesional && puedeGestionar && !editId) {
      if (!form.phone?.trim()) {
        setError("El teléfono de acceso es obligatorio para nuevos profesionales.");
        return;
      }
      if (!form.password || form.password.length < 6) {
        setError("La contraseña debe tener al menos 6 caracteres.");
        return;
      }
    }

    setError("");

    const url = editId ? `${API}/profesionales/${editId}` : `${API}/profesionales`;
    const method = editId ? "PUT" : "POST";

    const payload = {
      apellido: form.apellido,
      nombre: form.nombre,
      dni: form.dni || null,
      mail: form.mail || null,
      celular: form.celular || null,
      matricula: form.matricula || null,
      color: form.color,
      activo: form.activo,
    };

    if (multiProfesional && puedeGestionar) {
      if (!editId) {
        payload.phone = form.phone.trim();
        payload.password = form.password;
      } else {
        if (form.phone?.trim()) payload.phone = form.phone.trim();
        if (form.password) payload.password = form.password;
      }
    }

    try {
      const res = await fetch(url, {
        method,
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errData = await res.json();
        const validationMsg = errData.errors
          ? Object.values(errData.errors).flat().join(" ")
          : null;
        setError(validationMsg || errData.message || "Error al guardar");
        return;
      }

      await fetchProfesionales();
      await refreshSessionProfesional();
      setShowForm(false);
      setForm(emptyProfesional());
      setEditId(null);
    } catch (e) {
      setError("Error de conexión");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este profesional?")) return;
    try {
      await fetch(`${API}/profesionales/${id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      await fetchProfesionales();
    } catch (e) {
      console.error("Error al eliminar:", e);
    }
  };

  const filtrados = profesionales.filter((p) => {
    const q = busqueda.toLowerCase();
    return (
      p.apellido?.toLowerCase().includes(q) ||
      p.nombre?.toLowerCase().includes(q) ||
      p.dni?.includes(q) ||
      p.matricula?.toLowerCase().includes(q)
    );
  });

  /* ──────────────────────────────────────────────────────── */
  /* Vista para profesional: solo su propia ficha            */
  /* ──────────────────────────────────────────────────────── */
  if (!multiProfesional) {
    const miProf = resolveMiProf(profesionales, user);

    return (
      <div className="pp-container">
        <Sidebar />
        <div className="pp-content">
          <h2 className="pp-title">Mi perfil profesional</h2>

          {loadingProfesionales ? (
            <p className="prof-perfil-estado">Cargando datos…</p>
          ) : loadError ? (
            <div className="pp-card prof-perfil-vacio">
              <p className="prof-perfil-estado">{loadError}</p>
              <button type="button" className="pp-btn-agregar" onClick={fetchProfesionales}>
                Reintentar
              </button>
            </div>
          ) : !miProf && !showForm ? (
            <div className="pp-card prof-perfil-vacio">
              <p className="prof-perfil-estado">
                {profesionales.length === 0
                  ? "Todavía no hay un perfil profesional configurado para este consultorio."
                  : "No encontramos un perfil vinculado a tu cuenta."}
              </p>
              {puedeGestionar && (
                <button type="button" className="pp-btn-agregar" onClick={handleSetupPerfil}>
                  {profesionales.length === 0 ? "Crear mi perfil" : "Completar o vincular perfil"}
                </button>
              )}
            </div>
          ) : miProf || showForm ? (
            <>
              {!showForm && miProf ? (
                <ul className="pp-lista">
                  <li className="pp-card">
                    <div className="pp-card-header">
                      <h3 className="pp-card-nombre" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span
                          style={{
                            display: "inline-block",
                            width: 14,
                            height: 14,
                            borderRadius: "50%",
                            background: miProf.color || "#0A1B3D",
                            flexShrink: 0,
                          }}
                        />
                        {miProf.apellido}, {miProf.nombre}
                      </h3>
                      <span
                        style={{
                          fontSize: "0.8rem",
                          padding: "2px 10px",
                          borderRadius: 12,
                          background: miProf.activo ? "#E8F5E9" : "#FFEBEE",
                          color: miProf.activo ? "#166534" : "#991b1b",
                          fontWeight: 600,
                        }}
                      >
                        {miProf.activo ? "Activo" : "Inactivo"}
                      </span>
                    </div>

                    <div className="pp-info" style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: "8px 24px" }}>
                      {user?.phone && <span><b>Acceso (tel.):</b> {user.phone}</span>}
                      {miProf.matricula && <span><b>Mat.:</b> {miProf.matricula}</span>}
                      {miProf.dni && <span><b>DNI:</b> {miProf.dni}</span>}
                      {miProf.celular && <span><b>Cel:</b> {miProf.celular}</span>}
                      {miProf.mail && <span><b>Mail:</b> {miProf.mail}</span>}
                    </div>

                    <div className="pp-botones-inferiores" style={{ marginTop: 12 }}>
                      <button
                        onClick={() => handleEdit(miProf)}
                        className="boton-secundario boton-historia"
                      >
                        ✏️ Editar mis datos
                      </button>
                      <button
                        onClick={() => handleLicencias(miProf)}
                        className="boton-secundario boton-historia"
                      >
                        Mis licencias
                      </button>
                      <button
                        onClick={() => handleHorarios(miProf)}
                        className="boton-secundario boton-historia"
                      >
                        Horarios de atencion
                      </button>
                      <button
                        onClick={() => navigate(`/profesionales/${miProf.id}/prestaciones`)}
                        className="boton-secundario boton-historia"
                      >
                        💊 Prestaciones
                      </button>
                    </div>
                  </li>
                </ul>
              ) : showForm ? (
                <FormModal
                  editId={editId}
                  form={form}
                  error={error}
                  puedeGestionar={puedeGestionar}
                  multiProfesional={false}
                  handleChange={handleChange}
                  handleSave={handleSave}
                  onCancel={() => setShowForm(false)}
                />
              ) : null}
            </>
          ) : null}

          {licenciasProf && (
            <LicenciasModal
              profesional={licenciasProf}
              onClose={() => setLicenciasProf(null)}
            />
          )}
          {horariosProf && (
            <HorariosModal
              profesional={horariosProf}
              onClose={() => setHorariosProf(null)}
            />
          )}
        </div>
      </div>
    );
  }

  /* ──────────────────────────────────────────────────────── */
  /* Vista para admin: lista completa                        */
  /* ──────────────────────────────────────────────────────── */
  return (
    <div className="pp-container">
      <Sidebar />
      <div className="pp-content">
        <h2 className="pp-title">Profesionales</h2>

        <div className="pp-busqueda-container">
          <div className="pp-busqueda-wrapper">
            <span className="pp-busqueda-icon">🔍</span>
            <input
              type="text"
              placeholder="Buscar por apellido, nombre, DNI o matrícula..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="pp-busqueda-input"
            />
          </div>
          <button onClick={handleAdd} className="pp-btn-agregar">
            <span className="pp-btn-icon">+</span> Agregar Profesional
          </button>
        </div>

        <ul className="pp-lista">
          {filtrados.map((p) => (
            <li key={p.id} className="pp-card">
              <div className="pp-card-header">
                <h3
                  className="pp-card-nombre"
                  onClick={() => handleEdit(p)}
                  style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}
                  title="Editar profesional"
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      background: p.color || "#0A1B3D",
                      flexShrink: 0,
                    }}
                  />
                  {p.apellido}, {p.nombre}
                  <span style={{ fontSize: "0.8em", opacity: 0.7 }}>✏️</span>
                </h3>
                <span
                  style={{
                    fontSize: "0.8rem",
                    padding: "2px 10px",
                    borderRadius: 12,
                    background: p.activo ? "#E8F5E9" : "#FFEBEE",
                    color: p.activo ? "#166534" : "#991b1b",
                    fontWeight: 600,
                  }}
                >
                  {p.activo ? "Activo" : "Inactivo"}
                </span>
                <span
                  style={{
                    fontSize: "0.8rem",
                    padding: "2px 10px",
                    borderRadius: 12,
                    background: tieneAcceso(p) ? "#e0f2fe" : "#EEF1F5",
                    color: tieneAcceso(p) ? "#0369a1" : "#757575",
                    fontWeight: 600,
                  }}
                >
                  {tieneAcceso(p) ? "Con acceso" : "Sin acceso"}
                </span>
              </div>

              <div className="pp-info" style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: "8px 24px" }}>
                {p.user?.phone && <span><b>Acceso:</b> {p.user.phone}</span>}
                {p.matricula && <span><b>Mat.:</b> {p.matricula}</span>}
                {p.dni && <span><b>DNI:</b> {p.dni}</span>}
                {p.celular && <span><b>Cel:</b> {p.celular}</span>}
                {p.mail && <span><b>Mail:</b> {p.mail}</span>}
              </div>

              {!tieneAcceso(p) && (
                <p style={{ margin: "8px 0 0", fontSize: "0.85rem", color: "#B26A00" }}>
                  Sin acceso a la app — editá el profesional para activar teléfono y contraseña.
                </p>
              )}

              <div className="pp-botones-inferiores" style={{ marginTop: 12 }}>
                <button
                  onClick={() => handleEdit(p)}
                  className="boton-secundario boton-historia"
                >
                  ✏️ Editar
                </button>
                <button
                  onClick={() => handleLicencias(p)}
                  className="boton-secundario boton-historia"
                >
                  Licencias
                </button>
                <button
                  onClick={() => handleHorarios(p)}
                  className="boton-secundario boton-historia"
                >
                  Horarios
                </button>
                <button
                  onClick={() => navigate(`/profesionales/${p.id}/prestaciones`)}
                  className="boton-secundario boton-historia"
                >
                  💊 Prestaciones
                </button>
                <button
                  onClick={() => handleDelete(p.id)}
                  className="boton-secundario"
                  style={{ background: "#FFEBEE", color: "#991b1b" }}
                >
                  🗑️ Eliminar
                </button>
              </div>
            </li>
          ))}
          {filtrados.length === 0 && (
            <li style={{ padding: "24px", textAlign: "center", color: "#757575" }}>
              No hay profesionales registrados.
            </li>
          )}
        </ul>

        {showForm && (
          <FormModal
            editId={editId}
            form={form}
            error={error}
            puedeGestionar={puedeGestionar}
            multiProfesional={multiProfesional}
            handleChange={handleChange}
            handleSave={handleSave}
            onCancel={() => setShowForm(false)}
          />
        )}

        {licenciasProf && (
          <LicenciasModal
            profesional={licenciasProf}
            onClose={() => setLicenciasProf(null)}
          />
        )}
        {horariosProf && (
          <HorariosModal
            profesional={horariosProf}
            onClose={() => setHorariosProf(null)}
          />
        )}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────── */
/* Modal de edición de profesional                         */
/* ──────────────────────────────────────────────────────── */
function FormModal({ editId, form, error, puedeGestionar, multiProfesional, handleChange, handleSave, onCancel }) {
  const showAccessFields = multiProfesional && puedeGestionar;
  const activatingAccess = showAccessFields && editId && !tieneAcceso(form);

  return (
    <div className="modal-historia">
      <div className="modal-historia-content">
        <h2>{editId ? "Editar Profesional" : "Nuevo Profesional"}</h2>

        <div className="modal-historia-grid">
          <input
            name="apellido"
            placeholder="Apellido (*)"
            value={form.apellido}
            onChange={handleChange}
          />
          <input
            name="nombre"
            placeholder="Nombre (*)"
            value={form.nombre}
            onChange={handleChange}
          />
          <input
            name="dni"
            placeholder="DNI"
            value={form.dni}
            onChange={handleChange}
          />
          <input
            name="matricula"
            placeholder="Matrícula profesional"
            value={form.matricula}
            onChange={handleChange}
          />
          <input
            name="mail"
            type="email"
            placeholder="Mail"
            value={form.mail}
            onChange={handleChange}
          />
          <input
            name="celular"
            placeholder="Celular"
            value={form.celular}
            onChange={handleChange}
          />
        </div>

        {showAccessFields && (
          <div style={{ marginTop: 16 }}>
            <p style={{ margin: "0 0 10px", fontWeight: 600, color: "#334155" }}>
              {activatingAccess
                ? "Activar acceso a la app"
                : editId
                  ? "Acceso a la app"
                  : "Acceso a la app (*)"}
            </p>
            <div className="modal-historia-grid">
              <input
                name="phone"
                placeholder={editId ? "Teléfono de acceso (login)" : "Teléfono de acceso (*)"}
                value={form.phone}
                onChange={handleChange}
              />
              <input
                name="password"
                type="password"
                placeholder={
                  editId
                    ? activatingAccess
                      ? "Contraseña inicial (*)"
                      : "Nueva contraseña (opcional)"
                    : "Contraseña (*)"
                }
                value={form.password}
                onChange={handleChange}
              />
            </div>
            {editId && tieneAcceso(form) && (
              <p style={{ margin: "8px 0 0", fontSize: "0.85rem", color: "#757575" }}>
                Dejá la contraseña vacía si no querés cambiarla.
              </p>
            )}
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 16, margin: "12px 0" }}>
          <label style={{ fontWeight: 600 }}>
            Color en calendario:&nbsp;
            <input
              type="color"
              name="color"
              value={form.color}
              onChange={handleChange}
              style={{ cursor: "pointer", width: 40, height: 32, border: "none", padding: 0 }}
            />
          </label>

          {puedeGestionar && (
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 600 }}>
              <input
                type="checkbox"
                name="activo"
                checked={form.activo}
                onChange={handleChange}
              />
              Activo
            </label>
          )}
        </div>

        {error && (
          <p style={{ color: "#D32F2F", fontWeight: 600, margin: "8px 0" }}>{error}</p>
        )}

        <div className="modal-historia-buttons">
          <button onClick={onCancel} className="btn-cerrar">
            Cancelar
          </button>
          <button onClick={handleSave} className="btn-guardar">
            {editId ? "Guardar Cambios" : "Guardar Profesional"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────── */
/* Modal de licencias / días no disponibles                */
/* ──────────────────────────────────────────────────────── */
function LicenciasModal({ profesional, onClose }) {
  const [bloques, setBloques] = useState([]);
  const [bloquesLoading, setBloquesLoading] = useState(false);
  const [showNewBloque, setShowNewBloque] = useState(false);
  const [newBloque, setNewBloque] = useState({ fecha_inicio: "", fecha_fin: "", razon: "" });
  const [bloquesError, setBloquesError] = useState("");

  useEffect(() => {
    if (!profesional?.id) return;
    setBloquesLoading(true);
    fetch(`${API}/profesionales/${profesional.id}/bloques`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { if (d.success) setBloques(d.data); })
      .catch(() => {})
      .finally(() => setBloquesLoading(false));
  }, [profesional?.id]);

  const handleAddBloque = async () => {
    if (!newBloque.fecha_inicio || !newBloque.fecha_fin) {
      setBloquesError("Las fechas de inicio y fin son obligatorias.");
      return;
    }
    if (newBloque.fecha_fin < newBloque.fecha_inicio) {
      setBloquesError("La fecha de fin debe ser igual o posterior a la de inicio.");
      return;
    }
    setBloquesError("");
    try {
      const res = await fetch(`${API}/profesionales/${profesional.id}/bloques`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(newBloque),
      });
      const data = await res.json();
      if (data.success) {
        setBloques(prev => [...prev, data.data]);
        setNewBloque({ fecha_inicio: "", fecha_fin: "", razon: "" });
        setShowNewBloque(false);
      } else {
        setBloquesError(data.message || "Error al guardar el período.");
      }
    } catch {
      setBloquesError("Error de conexión.");
    }
  };

  const handleDeleteBloque = async (id) => {
    if (!window.confirm("¿Eliminar este período de licencia?")) return;
    try {
      const res = await fetch(`${API}/bloques/${id}`, { method: "DELETE", headers: authHeaders() });
      const data = await res.json();
      if (data.success) setBloques(prev => prev.filter(b => b.id !== id));
    } catch {}
  };

  const fmtRango = (inicio, fin) => {
    const a = fmtFecha(inicio);
    const b = fmtFecha(fin);
    if (a === b) return a;
    return `${a} – ${b}`;
  };

  return (
    <div className="modal-historia">
      <div className="modal-historia-content">
        <h2>Licencias y días no disponibles</h2>
        <p style={{ margin: "0 0 16px", color: "#757575", fontSize: "0.92rem" }}>
          {profesional.apellido}, {profesional.nombre}
        </p>

        {!showNewBloque && (
          <div style={{ marginBottom: 16 }}>
            <button
              onClick={() => { setShowNewBloque(true); setBloquesError(""); }}
              className="pp-btn-agregar"
              style={{ fontSize: "0.88rem" }}
            >
              + Agregar período
            </button>
          </div>
        )}

        {showNewBloque && (
          <div className="licencias-form">
            <p className="licencias-form__title">Nuevo período</p>
            <div className="licencias-form__fechas">
              <label className="licencias-form__label">
                Desde
                <input
                  type="date"
                  className="pp-busqueda-input"
                  value={newBloque.fecha_inicio}
                  onChange={e => setNewBloque(p => ({ ...p, fecha_inicio: e.target.value }))}
                />
              </label>
              <label className="licencias-form__label">
                Hasta
                <input
                  type="date"
                  className="pp-busqueda-input"
                  value={newBloque.fecha_fin}
                  onChange={e => setNewBloque(p => ({ ...p, fecha_fin: e.target.value }))}
                />
              </label>
            </div>
            <input
              className="pp-busqueda-input"
              value={newBloque.razon}
              onChange={e => setNewBloque(p => ({ ...p, razon: e.target.value }))}
              placeholder="Motivo (ej: Vacaciones, Licencia médica…)"
              style={{ marginBottom: 10 }}
            />
            {bloquesError && (
              <p className="licencias-form__error">{bloquesError}</p>
            )}
            <div className="modal-historia-buttons">
              <button
                onClick={() => {
                  setShowNewBloque(false);
                  setNewBloque({ fecha_inicio: "", fecha_fin: "", razon: "" });
                  setBloquesError("");
                }}
                className="btn-cerrar"
              >
                Cancelar
              </button>
              <button onClick={handleAddBloque} className="btn-guardar">
                Guardar período
              </button>
            </div>
          </div>
        )}

        {bloquesLoading ? (
          <p style={{ color: "#757575", fontSize: "0.9rem" }}>Cargando…</p>
        ) : bloques.length === 0 && !showNewBloque ? (
          <p className="licencias-empty">Sin períodos de licencia registrados.</p>
        ) : (
          <ul className="licencias-lista">
            {bloques.map(b => (
              <li key={b.id} className="licencias-item">
                <div className="licencias-item__info">
                  <span className="licencias-item__fechas">
                    {fmtRango(b.fecha_inicio, b.fecha_fin)}
                  </span>
                  {b.razon && (
                    <span className="licencias-item__motivo">{b.razon}</span>
                  )}
                </div>
                <button
                  onClick={() => handleDeleteBloque(b.id)}
                  className="licencias-item__eliminar"
                >
                  Eliminar
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="modal-historia-buttons" style={{ marginTop: 20 }}>
          <button onClick={onClose} className="btn-cerrar">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

const DIAS_SEMANA = [
  { value: 1, label: "Lunes" },
  { value: 2, label: "Martes" },
  { value: 3, label: "Miercoles" },
  { value: 4, label: "Jueves" },
  { value: 5, label: "Viernes" },
  { value: 6, label: "Sabado" },
  { value: 0, label: "Domingo" },
];

function HorariosModal({ profesional, onClose }) {
  const [horarios, setHorarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ dia_semana: 1, hora_inicio: "09:00", hora_fin: "13:00" });
  const [error, setError] = useState("");

  useEffect(() => {
    if (!profesional?.id) return;
    setLoading(true);
    fetch(`${API}/profesionales/${profesional.id}/horarios`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => {
        if (d.success) setHorarios(d.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [profesional?.id]);

  const labelDia = (dia) => DIAS_SEMANA.find((d) => d.value === Number(dia))?.label || `Dia ${dia}`;

  const handleAdd = async () => {
    if (!form.hora_inicio || !form.hora_fin) {
      setError("Completa el horario.");
      return;
    }
    if (form.hora_fin <= form.hora_inicio) {
      setError("La hora de fin debe ser posterior a la de inicio.");
      return;
    }
    setError("");
    try {
      const res = await fetch(`${API}/profesionales/${profesional.id}/horarios`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) {
        setHorarios((prev) => [...prev, data.data]);
        setShowNew(false);
        setForm({ dia_semana: 1, hora_inicio: "09:00", hora_fin: "13:00" });
      } else {
        setError(data.message || "Error al guardar el horario.");
      }
    } catch {
      setError("Error de conexion.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar este horario?")) return;
    try {
      const res = await fetch(`${API}/horarios/${id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      const data = await res.json();
      if (data.success) setHorarios((prev) => prev.filter((h) => h.id !== id));
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="modal-historia">
      <div className="modal-historia-content">
        <h2>Horarios de atencion</h2>
        <p style={{ margin: "0 0 16px", color: "#757575", fontSize: "0.92rem" }}>
          {profesional.apellido}, {profesional.nombre}
        </p>
        <p style={{ margin: "0 0 16px", color: "#757575", fontSize: "0.88rem" }}>
          Estos horarios se usan para la landing de autogestion de turnos.
        </p>

        {!showNew && (
          <div style={{ marginBottom: 16 }}>
            <button
              type="button"
              onClick={() => {
                setShowNew(true);
                setError("");
              }}
              className="pp-btn-agregar"
              style={{ fontSize: "0.88rem" }}
            >
              + Agregar franja
            </button>
          </div>
        )}

        {showNew && (
          <div className="licencias-form">
            <p className="licencias-form__title">Nueva franja</p>
            <div className="licencias-form__fechas">
              <label className="licencias-form__label">
                Dia
                <select
                  className="pp-busqueda-input"
                  value={form.dia_semana}
                  onChange={(e) => setForm((p) => ({ ...p, dia_semana: Number(e.target.value) }))}
                >
                  {DIAS_SEMANA.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="licencias-form__label">
                Desde
                <input
                  type="time"
                  className="pp-busqueda-input"
                  value={form.hora_inicio}
                  onChange={(e) => setForm((p) => ({ ...p, hora_inicio: e.target.value }))}
                />
              </label>
              <label className="licencias-form__label">
                Hasta
                <input
                  type="time"
                  className="pp-busqueda-input"
                  value={form.hora_fin}
                  onChange={(e) => setForm((p) => ({ ...p, hora_fin: e.target.value }))}
                />
              </label>
            </div>
            {error && <p className="licencias-form__error">{error}</p>}
            <div className="modal-historia-buttons">
              <button
                type="button"
                onClick={() => {
                  setShowNew(false);
                  setError("");
                }}
                className="btn-cerrar"
              >
                Cancelar
              </button>
              <button type="button" onClick={handleAdd} className="btn-guardar">
                Guardar franja
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <p style={{ color: "#757575", fontSize: "0.9rem" }}>Cargando…</p>
        ) : horarios.length === 0 && !showNew ? (
          <p className="licencias-empty">Sin horarios cargados.</p>
        ) : (
          <ul className="licencias-lista">
            {horarios.map((h) => (
              <li key={h.id} className="licencias-item">
                <div className="licencias-item__info">
                  <span className="licencias-item__fechas">
                    {labelDia(h.dia_semana)} · {String(h.hora_inicio).slice(0, 5)} –{" "}
                    {String(h.hora_fin).slice(0, 5)}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => handleDelete(h.id)}
                  className="licencias-item__eliminar"
                >
                  Eliminar
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="modal-historia-buttons" style={{ marginTop: 20 }}>
          <button type="button" onClick={onClose} className="btn-cerrar">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
